// Dashboard
//
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'
import {
  People as PeopleIcon,
  Timeline as TimelineIcon,
  Dashboard as DashboardIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material'
import MainChart from './MainChart'
import {
  fetchBiProductsAllowed,
  fetchBiSummary,
  fetchBiActivityTrend,
  fetchBiTopCodes,
} from '../../services/api'

const STORAGE_KEY = 'biDashboardFilters'
const DAYS_IN_RANGE = 365

const formatDate = (date) => date.toISOString().slice(0, 10)

const getDefaultRange = () => {
  const to = new Date()
  const from = new Date()
  from.setDate(to.getDate() - DAYS_IN_RANGE)
  return { from: formatDate(from), to: formatDate(to) }
}

const readStoredFilters = () => {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw)
    return {
      productId: parsed?.productId ? String(parsed.productId) : '',
      from: parsed?.from || '',
      to: parsed?.to || '',
    }
  } catch (error) {
    return null
  }
}

const persistFilters = (filters) => {
  if (typeof window === 'undefined') {
    return
  }
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
}

const logDev = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args)
  }
}

const Dashboard = () => {
  const storedFilters = useMemo(() => readStoredFilters(), [])
  const defaultRange = useMemo(() => getDefaultRange(), [])
  const [products, setProducts] = useState([])
  const [selectedProductId, setSelectedProductId] = useState(
    storedFilters?.productId || ''
  )
  const [dateRange, setDateRange] = useState({
    from: storedFilters?.from || defaultRange.from,
    to: storedFilters?.to || defaultRange.to,
  })
  const [summary, setSummary] = useState(null)
  const [trend, setTrend] = useState([])
  const [topCodes, setTopCodes] = useState({ topActions: [], topResults: [] })
  const [loading, setLoading] = useState({
    products: false,
    summary: false,
    trend: false,
    topCodes: false,
  })
  const [errors, setErrors] = useState({
    products: '',
    summary: '',
    trend: '',
    topCodes: '',
  })

  const getErrorMessage = useCallback((error, fallback) => {
    return error?.response?.data?.message || error?.message || fallback
  }, [])

  const getBiErrorMessage = useCallback(
    (error, fallback) => {
      const status = error?.response?.status
      if (status === 403) {
        return 'No tienes permisos para acceder a la información de BI.'
      }
      if (status === 404) {
        return 'No se encontró la información de BI solicitada.'
      }
      return getErrorMessage(error, fallback)
    },
    [getErrorMessage]
  )

  useEffect(() => {
    const loadProducts = async () => {
      setLoading((prev) => ({ ...prev, products: true }))
      setErrors((prev) => ({ ...prev, products: '' }))
      try {
        const response = await fetchBiProductsAllowed()
        const items = response?.products || []
        setProducts(items)
        if (items.length > 0) {
          const storedProductId = storedFilters?.productId
          const hasStoredProduct = items.some(
            (product) => String(product.id) === String(storedProductId)
          )
          setSelectedProductId(
            hasStoredProduct ? String(storedProductId) : String(items[0].id)
          )
        } else {
          setErrors((prev) => ({
            ...prev,
            products: 'No hay productos disponibles para este usuario.',
          }))
        }
      } catch (error) {
        setErrors((prev) => ({
          ...prev,
          products: getBiErrorMessage(
            error,
            'No se pudieron cargar los productos.'
          ),
        }))
      } finally {
        setLoading((prev) => ({ ...prev, products: false }))
      }
    }

    loadProducts()
  }, [storedFilters?.productId, storedFilters, getBiErrorMessage])

  useEffect(() => {
    if (!selectedProductId && !dateRange.from && !dateRange.to) {
      return
    }
    persistFilters({
      productId: selectedProductId,
      from: dateRange.from,
      to: dateRange.to,
    })
  }, [selectedProductId, dateRange.from, dateRange.to])

  useEffect(() => {
    if (!selectedProductId) {
      return
    }

    const loadSummary = async () => {
      setLoading((prev) => ({ ...prev, summary: true }))
      setErrors((prev) => ({ ...prev, summary: '' }))
      try {
        const data = await fetchBiSummary(selectedProductId)
        setSummary(data)
      } catch (error) {
        setErrors((prev) => ({
          ...prev,
          summary: getBiErrorMessage(
            error,
            'No se pudo cargar el resumen del producto.'
          ),
        }))
      } finally {
        setLoading((prev) => ({ ...prev, summary: false }))
      }
    }

    const loadTrend = async () => {
      setLoading((prev) => ({ ...prev, trend: true }))
      setErrors((prev) => ({ ...prev, trend: '' }))
      try {
        if (!dateRange.from || !dateRange.to) {
          setTrend([])
          return
        }
        const data = await fetchBiActivityTrend(
          selectedProductId,
          dateRange.from,
          dateRange.to
        )
        setTrend(data?.trend || [])
      } catch (error) {
        logDev('[Dashboard] Error al cargar tendencia BI:', error)
        setErrors((prev) => ({
          ...prev,
          trend: getBiErrorMessage(
            error,
            'No se pudo cargar la tendencia de actividad.'
          ),
        }))
      } finally {
        setLoading((prev) => ({ ...prev, trend: false }))
      }
    }

    const loadTopCodes = async () => {
      setLoading((prev) => ({ ...prev, topCodes: true }))
      setErrors((prev) => ({ ...prev, topCodes: '' }))
      try {
        const data = await fetchBiTopCodes(selectedProductId, 10)
        setTopCodes({
          topActions: data?.topActions || [],
          topResults: data?.topResults || [],
        })
      } catch (error) {
        setErrors((prev) => ({
          ...prev,
          topCodes: getBiErrorMessage(
            error,
            'No se pudieron cargar los códigos más frecuentes.'
          ),
        }))
      } finally {
        setLoading((prev) => ({ ...prev, topCodes: false }))
      }
    }

    loadSummary()
    loadTrend()
    loadTopCodes()
  }, [selectedProductId, dateRange.from, dateRange.to, getBiErrorMessage])

  const handleProductChange = (event) => {
    setSelectedProductId(event.target.value)
  }

  const handleDateChange = (field) => (event) => {
    const nextValue = event.target.value
    setDateRange((prev) => ({ ...prev, [field]: nextValue }))
  }

  const stats = [
    {
      title: 'Consumers',
      value: summary?.consumersCount ?? 0,
      icon: <PeopleIcon />,
      color: 'primary',
    },
    {
      title: 'Activities',
      value: summary?.activitiesCount ?? 0,
      icon: <TimelineIcon />,
      color: 'secondary',
    },
    {
      title: 'Active Layouts',
      value: summary?.layoutsCount ?? 0,
      icon: <DashboardIcon />,
      color: 'success',
    },
    {
      title: 'Documents',
      value: summary?.documentsCount ?? 0,
      icon: <DescriptionIcon />,
      color: 'warning',
    },
  ]

  return (
    <Container>
      <Typography variant="h4" component="h1" gutterBottom>
        Dashboard
      </Typography>

      <Box sx={{ marginBottom: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="product-select-label">Producto</InputLabel>
              <Select
                labelId="product-select-label"
                value={selectedProductId}
                label="Producto"
                onChange={handleProductChange}
                disabled={loading.products || products.length === 0}
              >
                {products.map((product) => (
                  <MenuItem key={product.id} value={String(product.id)}>
                    {product.name_product}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Desde"
              value={dateRange.from}
              onChange={handleDateChange('from')}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: dateRange.to }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Hasta"
              value={dateRange.to}
              onChange={handleDateChange('to')}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: dateRange.from }}
            />
          </Grid>
        </Grid>
        {errors.products && (
          <Alert severity="warning" sx={{ marginTop: 2 }}>
            {errors.products}
          </Alert>
        )}
      </Box>

      {errors.summary && (
        <Alert severity="error" sx={{ marginBottom: 2 }}>
          {errors.summary}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <MainChart
            trend={trend}
            loading={loading.trend}
            errorMessage={errors.trend}
            noDataMessage="No hay actividades para el producto seleccionado en el rango de fechas. Prueba ampliar el rango."
          />
        </Grid>
      </Grid>

      {errors.trend && (
        <Alert severity="error" sx={{ marginTop: 2 }}>
          {errors.trend}
        </Alert>
      )}

      <Grid container spacing={3}>
        {stats.map((stat, index) => (
          <Grid item xs={12} md={3} key={`${stat.title}-${index}`}>
            <Card sx={{ minWidth: 275, marginBottom: 2 }}>
              <CardContent>
                <Grid container alignItems="center">
                  <Grid
                    item
                    sx={{ marginRight: 1, color: `${stat.color}.main` }}
                  >
                    {stat.icon}
                  </Grid>
                  <Grid item>
                    <Typography variant="h5" component="h2">
                      {stat.value}
                    </Typography>
                    <Typography color="textSecondary" gutterBottom>
                      {stat.title}
                    </Typography>
                  </Grid>
                </Grid>
                <Box sx={{ marginTop: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(Number(stat.value) || 0, 100)}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ marginY: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top acciones
              </Typography>
              {loading.topCodes ? (
                <Typography>Cargando...</Typography>
              ) : errors.topCodes ? (
                <Alert severity="error">{errors.topCodes}</Alert>
              ) : (
                <List dense>
                  {topCodes.topActions.length === 0 ? (
                    <ListItem>
                      <ListItemText primary="Sin datos disponibles." />
                    </ListItem>
                  ) : (
                    topCodes.topActions.map((item) => (
                      <ListItem key={`action-${item.code}`}>
                        <ListItemText
                          primary={`${item.label} (${item.count})`}
                          secondary={`Código: ${item.code}`}
                        />
                      </ListItem>
                    ))
                  )}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top resultados
              </Typography>
              {loading.topCodes ? (
                <Typography>Cargando...</Typography>
              ) : errors.topCodes ? (
                <Alert severity="error">{errors.topCodes}</Alert>
              ) : (
                <List dense>
                  {topCodes.topResults.length === 0 ? (
                    <ListItem>
                      <ListItemText primary="Sin datos disponibles." />
                    </ListItem>
                  ) : (
                    topCodes.topResults.map((item) => (
                      <ListItem key={`result-${item.code}`}>
                        <ListItemText
                          primary={`${item.label} (${item.count})`}
                          secondary={`Código: ${item.code}`}
                        />
                      </ListItem>
                    ))
                  )}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  )
}

export default Dashboard
